import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
} from "@mui/material";
import { usePartyQuery } from "Api/Queries/Party/usePartyQuery";
import { useDeletePartyRelationMutation } from "Api/Mutations/Party/useDeletePartyRelationMutation";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { ConfirmDialog } from "Components/Shared/Dialogs/ConfirmDialog";
import { RelatedPartyForm } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/RelatedPartyForm";
import { RelatedPartyReadOnly } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/RelatedPartyReadOnly";
import { RelatedPartyRowSkeleton } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/RelatedPartyRowSkeleton";
import { RelatedPartySummaryContent } from "Components/Users/UserDetailPageContent/Components/RelatedPartyForm/RelatedPartySummaryContent";
import { useConfirmDialog } from "Hooks/useConfirmDialog";
import { useCheckPermission } from "Hooks/usePermission";
import { Permission, type RelationType } from "Infrastructure/Api/Api";
import { AccessRightCodes } from "Models/AccessRightCodes";
import { getWorstDocumentExpirationSeverity } from "Utils/DocumentExpiration/documentExpirationUtils";
import { useState } from "react";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const COMPONENT_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.RelatedParties.Form
    .Controls;

const DELETE_RESOURCES =
  Resources.Pages.Content.UserDetailPageContent.Tabs.RelatedParties.Delete;

type RelatedPartyFormWrapperProps = {
  relatedPartyPublicID: string;
  relationType: RelationType;
  isOwner: boolean;
  isReadOnly?: boolean;
  owningPartyID?: string | null;
  relationPublicID?: string | null;
};

export const RelatedPartyFormWrapper = ({
  relatedPartyPublicID,
  isOwner,
  relationType,
  isReadOnly,
  owningPartyID,
  relationPublicID,
}: RelatedPartyFormWrapperProps) => {
  const [expanded, setExpanded] = useState(false);
  const [wasExpanded, setWasExpanded] = useState(false);
  const { t } = useResource();

  const handleExpandedChange = (isExpanded: boolean) => {
    setExpanded(isExpanded);
    if (isExpanded) {
      setWasExpanded(true);
    }
  };
  const { data: relatedPartyData, isLoading } =
    usePartyQuery(relatedPartyPublicID);

  const canDelete = useCheckPermission(
    AccessRightCodes.Parties,
    Permission.Delete,
  );
  const deleteConfirm = useConfirmDialog();
  const { mutate: deleteRelation, isPending: isDeleting } =
    useDeletePartyRelationMutation({
      owningPartyPublicID: owningPartyID,
      onSuccess: () => {
        toast.success(t(DELETE_RESOURCES.SuccessMessage));
        setExpanded(false);
      },
      onError: () => toast.error(t(DELETE_RESOURCES.ErrorMessage)),
    });

  const isDeletable =
    !isReadOnly && canDelete && relationPublicID && owningPartyID;

  if (isLoading) {
    return <RelatedPartyRowSkeleton />;
  }

  if (!relatedPartyData) {
    return <>{t(COMPONENT_RESOURCES.LoadingError)}</>;
  }

  const fullName =
    [relatedPartyData.firstName, relatedPartyData.lastName]
      .filter(Boolean)
      .join(" ") || "";

  const documentExpirationSeverity = getWorstDocumentExpirationSeverity([
    relatedPartyData.partyIdentification?.identificationValidTo,
    relatedPartyData.partyIdentification?.secondIdentificationValidTo,
  ]);

  return (
    <>
      <Accordion
        disableGutters
        elevation={0}
        expanded={expanded}
        onChange={(_event, isExpanded) => handleExpandedChange(isExpanded)}
        sx={({ palette }) => ({
          border: `1px solid ${palette.divider}`,
          borderBottom: "0",
          "&:last-child": {
            borderBottom: `1px solid ${palette.divider}`,
          },
          "&::before": {
            display: "none",
          },
        })}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls={`panel-${relatedPartyPublicID}-content`}
          id={`panel-${relatedPartyPublicID}-header`}
          sx={{ backgroundColor: "background.paper" }}
        >
          <RelatedPartySummaryContent
            fullName={fullName}
            relationType={relationType}
            hasIdentificationsUploaded={
              !!relatedPartyData.hasIdentificationsUploaded
            }
            isOwner={isOwner}
            documentExpirationSeverity={documentExpirationSeverity}
          />
        </AccordionSummary>
        <AccordionDetails
          sx={({ spacing }) => ({
            padding: spacing(5),
            borderTop: "1px solid rgba(0, 0, 0, .05)",
          })}
        >
          {isReadOnly ? (
            <RelatedPartyReadOnly
              relationType={relationType}
              partyData={relatedPartyData}
            />
          ) : (
            /* Defer mounting the form (and its address-registry verification query) until
               the row is first opened, then keep it mounted so edits survive a collapse. */
            wasExpanded && (
              <>
                <RelatedPartyForm
                  relationType={relationType}
                  partyData={relatedPartyData}
                  isOwner={isOwner}
                  onSaveSuccess={() => setExpanded(false)}
                />
                {isDeletable && (
                  <Box mt={3}>
                    <PrimaryButton
                      fullWidth
                      variant="outlined"
                      color="error"
                      isLoading={isDeleting}
                      onClick={deleteConfirm.open}
                    >
                      {t(DELETE_RESOURCES.Button)}
                    </PrimaryButton>
                  </Box>
                )}
              </>
            )
          )}
        </AccordionDetails>
      </Accordion>
      {isDeletable && (
        <ConfirmDialog
          isDestructive
          isOpen={deleteConfirm.isOpen}
          modalTitle={t(DELETE_RESOURCES.ConfirmTitle)}
          modalContent={t(DELETE_RESOURCES.ConfirmContent, { name: fullName })}
          confirmButtonText={t(DELETE_RESOURCES.Button)}
          handleResult={deleteConfirm.handleResult(() => {
            if (relationPublicID) {
              deleteRelation(relationPublicID);
            }
          })}
        />
      )}
    </>
  );
};
